import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import {
  formatDayTime,
  ORDER_KIND_LABEL,
  RACK_TYPE_FULL,
  statusMeta,
  type Unit,
  UNIT_SOURCE_LABEL,
  UNIT_STATE_LABEL,
  type OrderKind,
  type OrderStatus,
} from '@reikraft/shared';
import { ErrorBox, Loader, Panel, StatusBadge, useToast } from '@reikraft/ui';
import { api, errorText } from '../api';
import { PageHead } from '../components/common';
import { useTz } from '../session';

interface UnitCard {
  unit: Unit;
  events: { at: string; text: string; orderId: string | null; orderNumber: string | null; actor: string | null }[];
  orders: { id: string; number: string; status: OrderStatus; kind: OrderKind; createdAt: string }[];
}

/** Карточка рейки — сюда ведёт QR-код с бирки */
export function UnitPage() {
  const { code, unitId } = useParams();
  const tz = useTz();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['unit', code ?? unitId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<UnitCard>(code ? `/admin/units/by-code/${encodeURIComponent(code)}` : `/admin/units/${unitId}`),
  });
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<Unit>(`/admin/units/${q.data!.unit.id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast('Сохранено');
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });
  if (q.isLoading) return <Loader />;
  if (q.isError || !q.data) return <ErrorBox error={q.error} />;
  const { unit: u, events, orders } = q.data;
  const active = orders.find((o) => !['done', 'cancelled', 'declined'].includes(o.status));
  return (
    <>
      <PageHead
        crumbs={[{ label: 'Склад', to: '/stock' }, { label: `Бирка ${u.code}` }]}
        title={
          <h1 className="rk-mono" style={{ margin: 0, fontSize: 28 }}>
            Бирка {u.code}
          </h1>
        }
        sub={`${u.rackType ? RACK_TYPE_FULL[u.rackType] : 'Тип не указан'}${u.oem ? ` · ${u.oem}` : ''}`}
        actions={
          <>
            {active && (
              <Link to={`/orders/${active.id}`} className="rk-btn rk-btn--primary">
                Заказ {active.number} →
              </Link>
            )}
            <a className="rk-btn" href={`/api/print/units/${u.id}/label`} target="_blank" rel="noreferrer">
              Печать бирки
            </a>
          </>
        }
      />
      <div className="adm-cols">
        <div className="adm-cols__main">
          <Panel title="Рейка" bodyless>
            <dl className="rk-dl">
              <div>
                <dt>Состояние</dt>
                <dd>{UNIT_STATE_LABEL[u.state]}</dd>
              </div>
              <div>
                <dt>Владелец</dt>
                <dd>{u.ownerKind === 'company' ? 'Компания (фонд)' : (u.ownerName ?? 'Клиент')}</dd>
              </div>
              <div>
                <dt>Откуда</dt>
                <dd>
                  {UNIT_SOURCE_LABEL[u.source]}
                  {u.sourceOrder && (
                    <>
                      {' · '}
                      <Link to={`/orders/${u.sourceOrder.id}`} className="rk-accent rk-mono">
                        {u.sourceOrder.number}
                      </Link>
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt>Позиция витрины</dt>
                <dd>
                  {u.productId ? (
                    <Link to={`/stock/${u.productId}`} className="rk-accent rk-mono">
                      {u.productSku}
                    </Link>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div>
                <dt>Стенд</dt>
                <dd>{u.bench ? `${u.bench.number} · ${u.bench.passed ? 'пройден' : 'не пройден'}` : '—'}</dd>
              </div>
              <div>
                <dt>Резерв</dt>
                <dd>{u.reservation ? `${u.reservation.forName ?? u.reservation.orderNumber ?? 'да'}` : '—'}</dd>
              </div>
            </dl>
            {u.ownerKind === 'company' && (
              <div className="rk-panel__foot rk-grid-form">
                <label className="rk-field">
                  Место хранения
                  <input
                    className="rk-input rk-input--mono"
                    defaultValue={u.place ?? ''}
                    onBlur={(e) => e.target.value !== (u.place ?? '') && patch.mutate({ place: e.target.value || null })}
                  />
                </label>
                <label className="rk-field">
                  Состояние на складе
                  <select
                    className="rk-select"
                    value={['in_stock', 'ready', 'donor', 'scrapped'].includes(u.state) ? u.state : ''}
                    disabled={!['in_stock', 'ready', 'donor', 'scrapped'].includes(u.state)}
                    onChange={(e) => patch.mutate({ state: e.target.value })}
                  >
                    <option value="" disabled>
                      {UNIT_STATE_LABEL[u.state]}
                    </option>
                    <option value="in_stock">На складе, ждёт ремонта</option>
                    <option value="ready">Готова</option>
                    <option value="donor">Донор (на запчасти)</option>
                    <option value="scrapped">Утиль</option>
                  </select>
                </label>
                {u.state === 'in_stock' && (
                  <Link to={`/orders/new?kind=stock`} className="rk-btn" style={{ alignSelf: 'end' }}>
                    Восстановить для фонда
                  </Link>
                )}
              </div>
            )}
          </Panel>
          <Panel title="Заказы по рейке" bodyless>
            <div className="adm-cards" style={{ border: 0 }}>
              {orders.map((o) => (
                <Link key={o.id} to={`/orders/${o.id}`} className="adm-card">
                  <span className="adm-card__top">
                    <b className="rk-mono">{o.number}</b>
                    <StatusBadge status={o.status} kind={o.kind} />
                  </span>
                  <span className="adm-card__sub">
                    <span>{ORDER_KIND_LABEL[o.kind]}</span>
                    <span className="rk-mono">{formatDayTime(o.createdAt, tz)}</span>
                  </span>
                </Link>
              ))}
              {!orders.length && <div className="rk-empty">Заказов нет</div>}
            </div>
          </Panel>
        </div>
        <div className="adm-cols__side">
          <Panel title="История" bodyless>
            <ol className="adm-log">
              {events.map((e, i) => (
                <li key={i}>
                  <time>{formatDayTime(e.at, tz)}</time>
                  <span>
                    {e.text}
                    {e.orderNumber && e.orderId && (
                      <>
                        {' · '}
                        <Link to={`/orders/${e.orderId}`} className="rk-mono rk-accent">
                          {e.orderNumber}
                        </Link>
                      </>
                    )}
                    {e.actor && <span className="adm-log__actor"> · {e.actor}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </Panel>
        </div>
      </div>
    </>
  );
}

export const statusName = (s: OrderStatus, k: OrderKind) => statusMeta(s, k).name;
